import React, { useEffect, useRef } from 'react';
import styles from './Toast.module.scss';
import { CheckCircle2, XCircle, X } from 'lucide-react';

export interface ToastProps {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({
  message,
  type,
  onClose,
  duration = 2000,
}) => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Only auto-close success toasts, not error toasts
  useEffect(() => {
    if (type === 'success') {
      timerRef.current = setTimeout(() => {
        onClose();
      }, duration);

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }
  }, [type, duration, onClose]);

  // Handle click outside to close immediately
  const handleOverlayClick = () => {
    // Clear the auto-close timer if it exists
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div
        className={`${styles.modal} ${styles[type]}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.icon}>
          {type === 'success' ? (
            <CheckCircle2 size={56} />
          ) : (
            <XCircle size={56} />
          )}
        </div>
        <p className={styles.message} style={{ whiteSpace: 'pre-line' }}>
          {message}
        </p>
        {type === 'error' && (
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </div>
  );
};

export default Toast;
